import { Country, InlineAlertType, Query } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { useStepStartedFocusAnalytics } from 'features/application/hooks/useStepStartedFocusAnalytics';
import {
  UpdateApplicationData,
  UpdateApplicationHandler,
} from 'features/application/hooks/useUpdateApplication';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import GetCountries from 'services/graphql/queries/GetCountries';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import VXLSearchField from 'shared/components/vxl/input/VXLSearchField/VXLSearchField';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { UseMutationState, useQuery } from 'urql';

type CitizenshipRoute =
  '/reactivation/review-info' | '/signup/employment' | '/signup/verify-ssn';

interface CitizenshipScreenProps {
  updateApplication: UpdateApplicationHandler;
  fetching: boolean;
  error?: Error | null;
  isUpdateApplicationLoading: boolean;
  updateApplicationResult: UseMutationState<UpdateApplicationData>;
  isUpdateApplicationSuccess: boolean;
  reExecuteQuery: () => void;
  nextRoute: CitizenshipRoute;
  backRoute: CitizenshipRoute;
  isReactivation?: boolean;
  hasApplicationLoaded: boolean;
  isReapplication: boolean;
}

const Citizenship = ({
  updateApplication,
  fetching,
  error,
  isUpdateApplicationLoading,
  isUpdateApplicationSuccess,
  updateApplicationResult,
  reExecuteQuery,
  nextRoute,
  backRoute,
  isReactivation = false,
  hasApplicationLoaded,
  isReapplication,
}: CitizenshipScreenProps) => {
  const router = useRouter();
  const [{ data }, refetchCountries] = useQuery<Query>({
    query: GetCountries,
    requestPolicy: 'cache-first',
  });
  const [toShowSearch, setShowSearch] = useState(false);
  const [countrySelected, setCountrySelected] = useState<string | undefined>();
  const [country, setCountry] = useState('');
  const { t: translatedText } = useTranslation('signup', {
    keyPrefix: 'citizenship',
  });

  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();

  const [filteredList, setFilteredList] = useState<Country[]>([]);

  useEffect(() => {
    setFilteredList(data?.countries ?? []);
  }, [data]);

  const handleCitizenshipAnalytics = usePressableAnalytics({
    analyticsId: 'citizenshipSelected',
    customAttributes: {
      citizenship_selected: countrySelected,
    },
  });

  useEffect(() => {
    if (countrySelected) {
      handleCitizenshipAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countrySelected]);

  useStepStartedFocusAnalytics(
    hasApplicationLoaded
      ? {
          analyticsId: 'stepStarted',
          customAttributes: {
            step_name: 'personal_info_citizenship',
            ...(isReactivation && {
              reactivation_flow: true,
            }),
            ...(isReapplication && {
              reapplication_flow: 'true',
            }),
          },
        }
      : undefined,
  );

  useStepStartedFocusAnalytics(
    isUpdateApplicationSuccess
      ? {
          analyticsId: 'citizenshipStepCompleted',
          customAttributes: {
            step_name: 'personal_info_citizenship',
            ...(isReactivation && {
              reactivation_flow: true,
            }),
            ...(isReapplication && {
              reapplication_flow: 'true',
            }),
          },
        }
      : undefined,
  );

  const handleBack = useCallback(() => {
    router.replace(backRoute);
  }, [backRoute, router]);

  useBackHandler(handleBack);

  const onSubmit = async (countryCode: string) => {
    await updateApplication({
      updates: { countryOfCitizenship: countryCode },
      nextRoute: nextRoute,
    });
  };

  return (
    <>
      <ScreenTransitionOverlay
        isLoading={fetching || isUpdateApplicationLoading}
        LoadingComponent={
          <View style={styles.loaderContainer}>
            <LoadingSpinner />
          </View>
        }
        ErrorComponent={
          error ? (
            <DefaultPageDownErrorMessage
              error={error}
              title={defaultTitle}
              message={defaultMessage}
              illustrationKey="PLACEHOLDER_A"
              tryAgainAction={() => {
                reExecuteQuery();
                refetchCountries();
              }}
              screenAnalyticsEventKey="citizenshipError"
            />
          ) : null
        }
      />
      <View style={styles.container}>
        <Stack.Screen
          options={{
            headerShown: true,
            headerBackVisible: true,
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar {...props} primaryOnPress={handleBack} />
            ),
          }}
        />
        {updateApplicationResult.error ? (
          <View style={styles.inlineAlertContainer}>
            <InlineAlert
              alertType={InlineAlertType.Error}
              bodyText={translatedText('inlineAlert.errorTitle')}
              iconKeyType="ERROR"
              onDismiss={() => {}}
            />
          </View>
        ) : null}
        <View style={styles.citizenshipContainer}>
          <View style={styles.citizenshipFormContainer}>
            <TitleLgText
              numberOfLines={0}
              style={[styles.heading, styles.dividerContainer]}
            >
              {translatedText('title')}
            </TitleLgText>

            <BodyBaseText
              numberOfLines={0}
              style={[styles.describeStyle, styles.dividerContainer]}
            >
              {translatedText('description')}
            </BodyBaseText>
            {toShowSearch &&
            Array.isArray(data?.countries) &&
            !(fetching || isUpdateApplicationLoading) ? (
              <View style={styles.dividerContainer}>
                <VXLSearchField
                  testID="text-input-country-search"
                  options={(filteredList ?? []).map((item) => ({
                    key: item.countryCode,
                    label: item.name,
                    value: item,
                  }))}
                  labelText={translatedText('country')}
                  onSelectOption={async (item) => {
                    setCountrySelected(item.name);
                    await onSubmit(item.countryCode);
                  }}
                  isLoading={false}
                  placeholder={translatedText('search')}
                  value={country}
                  onChangeText={(value) => {
                    setFilteredList(
                      data.countries.filter((item) =>
                        item.name.toLowerCase().includes(value.toLowerCase()),
                      ),
                    );
                    setCountry(value);
                  }}
                />
              </View>
            ) : null}
            {!toShowSearch ? (
              <>
                <ListItem
                  primaryCopy={translatedText('citizen')}
                  analyticsId="citizenshipSelected"
                  customAttributes={{ citizenship_selected: 'USA' }}
                  onPress={() => onSubmit('USA')}
                />
                <View style={styles.dividerContainer}>
                  <HairLineDivider />
                </View>
                <ListItem
                  primaryCopy={translatedText('noncitizen')}
                  onPress={() => setShowSearch(true)}
                />
              </>
            ) : null}
          </View>
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  citizenshipContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingVertical: theme.spacing.m,
  },
  citizenshipFormContainer: {
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-start',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    display: 'flex',
    flex: 1,
    minHeight: `100%`,
    minWidth: `100%`,
  },
  describeStyle: {
    paddingBottom: theme.spacing.m,
  },
  dividerContainer: {
    marginHorizontal: theme.spacing.m,
  },
  heading: {
    marginBottom: theme.spacing.m,
  },
  inlineAlertContainer: {
    padding: theme.spacing.m,
  },
  loaderContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
});

export default Citizenship;
